import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'; // Add OverlayTrigger and Tooltip
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Import FontAwesome
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'; // Import info circle icon

import appStyle from '../../styles/App.module.scss';
import { Link, useHistory } from 'react-router-dom';
import TermsConditionsModal from '../CustomModal';
import GoogleButton from '../GoogleButton';
import { requestGoogleForm } from '../../services/google/google';
import { MAIN_DOMAIN } from '../../constants/constants';
import {
  loginWithGoogle,
  signupWithEmail,
} from '../../context/authContext/authActions';
import { useAuthContext } from '../../context/authContext';
import useEventListener from '../../hooks/useEventListener';
import Logo from '../Logo';
import CustomButton from '../CustomButton';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import AlertBounceDismissible from '../AlertBounceDismissible';
import CustomSpinner from '../CustomSpinner';
import signInStyle from '../../styles/SignIn.module.scss';
import CleverButton from '../CleverButton';
import styles from '../../styles/Dashboard.module.scss';
import { PRODUCT_SUPPORT_EMAIL } from '../../constants/constants';

import DownloadLinks from '../../components/DownloadLinks';

import TermsAndConditions_en from '../TermsAndConditions_en';
import TermsAndConditions_es from '../TermsAndConditions_es';

import LanguageSelector from '../LanguageSelector';
import { useTranslation } from 'react-i18next';

// Add CSS for the icon positioning
const customStyles = {
  iconWrapper: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
  },
};

const YearSelect = ({ value, onChange, onCloseModal, open }) => {
  const { t } = useTranslation();
  const YEAR_RANGE = 20; // Show the last 20 years in the dropdown
  const yearOptions = React.useMemo(
    () =>
      Array.from({
        length:
          new Date().getFullYear() - (new Date().getFullYear() - YEAR_RANGE),
      }).map((_, i) => {
        const yearInt = (new Date().getFullYear() - i).toString();
        return (
          <Button
            className={[
              signInStyle['custom-form-group'],
              'form-group',
              signInStyle['sign-in-button-external'],
              'custom-button-default',
              'text-white',
            ]}
            key={i}
            onClick={() =>
              onChange({
                target: { name: 'youngest_child_yob', value: yearInt },
              })
            }
            variant="outline-primary"
          >
            {yearInt.toString()}
          </Button>
        );
      }),
    [t]
  );
  return (
    <Modal show={open} onHide={onCloseModal}>
      <Modal.Header closeButton>
        <Modal.Title>{t('youngestChildYOB')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="flex-1-container align-items-center vh-100">
        {yearOptions.map((option) => option)}
      </Modal.Body>
    </Modal>
  );
};

const SignUpForm = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();

  const {
    dispatch,
    state: { error },
  } = useAuthContext();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [youngestChildYobError, setYoungestChildYobError] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const formRef = useRef(null);

  const [inputs, setInputs] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    tos_accepted: true,
    professional_id: '',
    registered_role: 0,
    youngest_child_yob: null,
    registration_code: '',
  });
  const [isTCAccepted, setIsTCAccepted] = useState(false);
  const [checkTCAccepted, setCheckIsTCAccepted] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [step1Visibility, setStep1Visibility] = useState(true);
  const [step2Visibility, setStep2Visibility] = useState(false);

  const _onAcceptTC = (isTermsAccepted) => {
    if (isTermsAccepted) {
      setIsTCAccepted(true);
    } else {
      setIsTCAccepted(false);
    }
  };

  const toggle = () => setModal(!modal);

  const newWindowRef = useRef(null);

  const onMessageReceiveFromBackend = React.useCallback(
    async (event) => {
      if (event.origin.startsWith(MAIN_DOMAIN)) {
        if (event.data.user) {
          const data = await loginWithGoogle(dispatch, event.data.user);
          if (newWindowRef.current) {
            newWindowRef.current.window.close();
          }

          if (!data) {
            setShowAlert(true);
          }

          history.push('/dashboard');
        }
      } else {
        // console.log('Unauthorized event!', event);
      }

      // eslint-disable-next-line
    },
    [dispatch]
  );

  useEffect(() => {
    if (!showAlert) {
      setShowAlert(true);
    }

    // eslint-disable-next-line
  }, [error]);

  useEventListener(onMessageReceiveFromBackend);

  const _requestGoogleSignupForm = () => {
    if (isTCAccepted) {
      setCheckIsTCAccepted('');
      newWindowRef.current = requestGoogleForm();
    } else {
      setCheckIsTCAccepted(t('check_terms'));
    }
  };

  const SignUpWithGoogleButton = () => {
    return (
      <GoogleButton
        onClick={_requestGoogleSignupForm}
        text="Sign up with Google"
      />
    );
  };
  const SignUpWithCleverButton = () => {
    return <CleverButton onClick={cleaverLogin} text="Sign up with Clever" />;
  };
  const cleaverLogin = (ev) => {
    if (isTCAccepted) {
      setCheckIsTCAccepted('');
      window.location.href =
        'https://schools.clever.com/applications/add/bounceback-dev';
    } else {
      setCheckIsTCAccepted(t('check_terms'));
    }
  };
  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    if (name.includes('youngest_child_yob')) {
      const sanitized = value.replace(/[^0-9]/g, '').slice(0, 4);
      const numValue = sanitized === '' ? '' : parseInt(sanitized);
      if (sanitized.length === 5) return;
      setInputs((prevState) => ({
        ...prevState,
        [name]: sanitized === '' ? '' : numValue,
      }));
      setOpenModal(false);
      return;
    }
    if (name.includes('registered_role')) {
      setInputs((prevState) => ({ ...prevState, [name]: parseInt(value) }));
      return;
    }
    setInputs((prevState) => ({ ...prevState, [name]: value }));
  };

  const onChangeHandlerPhone = (e) => {
    const { name, value } = e.target;

    // Auto-format phone number
    let digits = value.replace(/\D/g, ''); // Remove non-digits
    if (digits.length > 10) digits = digits.slice(0, 10); // Limit to 10 digits
    let formatted = digits;
    if (digits.length >= 3 && digits.length < 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length >= 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
        6
      )}`;
    }
    setInputs({ ...inputs, [name]: formatted });
  };

  const onNextStep = (e) => {
    e.preventDefault();
    if (
      document.getElementById('first_name_field').checkValidity() &&
      document.getElementById('last_name_field').checkValidity() &&
      document.getElementById('email_field').checkValidity() &&
      document.getElementById('phone_field').checkValidity() &&
      document.getElementById('registered_role_field').checkValidity()
    ) {
      setStep1Visibility(false);
      setStep2Visibility(true);
    } else {
      setPasswordError(t('signup_step_error'));
      document.getElementById('registered_role_field').reportValidity();
      document.getElementById('phone_field').reportValidity();
      document.getElementById('email_field').reportValidity();
      document.getElementById('last_name_field').reportValidity();
      document.getElementById('first_name_field').reportValidity();
    }
  };

  useEffect(() => {
    setPasswordError('');
    setPhoneError('');
    setSuccessMessage('');
    setYoungestChildYobError('');
  }, []);

  const validatePasswordComplexity = (password) => {
    // Better validation, to implement in August 2024
    // const complexityRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    // Old validation
    const complexityRegex = /^.{6,}$/;
    return complexityRegex.test(password);
  };

  const handleSignupWithEmail = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setPasswordError('');
    setPhoneError('');
    setSuccessMessage('');
    setYoungestChildYobError('');

    const {
      first_name,
      last_name,
      tos_accepted,
      email,
      phone,
      password,
      confirmPassword,
      registration_code,
      professional_id,
      registered_role,
      youngest_child_yob,
    } = inputs;

    // Validate if phone is empty
    if (!phone) {
      setPhoneError(t('phone_empty'));
      setIsLoading(false);
      return;
    }

    // Validate if password is empty
    if (!password) {
      setPasswordError(t('password_empty'));
      setIsLoading(false);
      return;
    }

    // Validate if password and confirmPassword match
    if (password !== confirmPassword) {
      setPasswordError(t('password_not_match'));
      setIsLoading(false);
      return;
    }

    // Validate password complexity
    if (!validatePasswordComplexity(password)) {
      // Better validation, to implement in August 2024
      // setPasswordError(
      //   'Password complexity requirement not met. Must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character.'
      // );

      setPasswordError(t('password_long'));

      setIsLoading(false);
      return;
    }

    // Validate if Year of Birth is a valid number if the user is a parent
    if (registered_role === 1 && !youngest_child_yob) {
      document.getElementById(`youngest_child_yob`).checkValidity();
      setYoungestChildYobError(t('youngestChildYOBInvalid'));
      setIsLoading(false);
      return;
    }

    const payload = {
      user: {
        first_name,
        last_name,
        tos_accepted,
        email,
        phone,
        password,
        password_confirmation: confirmPassword,
        registration_code,
        professional_id,
        registered_role,
        youngest_child_yob,
      },
    };

    try {
      // const user = { id: 12 };
      const user = await signupWithEmail(dispatch, payload);
      setIsLoading(false);
      if (user && user.id) {
        setSuccessMessage(t('registration_successful'));
      } else {
        setStep1Visibility(true);
        setStep2Visibility(false);
      }
    } catch (error) {
      setIsLoading(false);
      setPasswordError(t('unexpected_error'));
      setStep1Visibility(true);
      setStep2Visibility(false);
      console.error('Signup error:', error);
    }
  };

  const onCloseAlert = () => {
    setShowAlert((prevShowAlert) => !prevShowAlert);
  };
  const Errors = () => {
    let output = '';

    if (typeof error === 'object') {
      for (const key in error) {
        if (Object.hasOwnProperty.call(error, key)) {
          const sError = error[key];
          output += `${sError}\n`;
        }
      }
    } else {
      output = error || t('unexpected_error');
    }

    return (
      <AlertBounceDismissible
        cls={`alert-bounce alert-bounce-warning`}
        onCloseAlert={onCloseAlert}
        showAlert={showAlert}
        content={output}
        icon={faExclamationTriangle}
      />
    );
  };

  const isFormValid = React.memo(() => {
    return (
      !inputs.email ||
      !inputs.password ||
      !inputs.confirmPassword ||
      !inputs.first_name ||
      !inputs.last_name ||
      !isTCAccepted ||
      !inputs.youngest_child_yob ||
      inputs.password !== inputs.confirmPassword ||
      inputs.password.length < 6
    );
  });

  // Tooltip render function
  const renderTooltip = (props) => (
    <Tooltip id="professional-id-tooltip" {...props}>
      {t('professional_id_tooltip')}
    </Tooltip>
  );

  return (
    <>
      <div>
        <YearSelect
          onChange={onChangeHandler}
          onCloseModal={() => setOpenModal(false)}
          open={openModal}
        />
        <div style={{ width: 75, float: 'right', padding: 4 }}>
          <LanguageSelector />
        </div>
        <div className={signInStyle['form-wrapper']}>
          <div className={appStyle['form-header-container']}>
            <Logo />
          </div>

          {/* TODO: restore google and clever links */}
          <div className="text-center mt-4">
            {/*<div className="mb-3">
            <SignUpWithGoogleButton />
          </div>

          <SignUpWithCleverButton />*/}
          </div>

          {/* <p className="font-poppins my-4 font-18 fw-500 text-center text-color-primary"> */}
          {/* TODO: restore oauth buttons after text */}
          {/*or continue with your email*/}
          {/* </p> */}

          {error && <Errors />}
          <Form ref={formRef} onSubmit={handleSignupWithEmail}>
            <div
              id="step1"
              style={{ display: step1Visibility ? 'block' : 'none' }}
            >
              <div className={signInStyle['b-form-body']}>
                <div className={signInStyle['form-names-container']}>
                  <Form.Group className={signInStyle['custom-form-group']}>
                    <Form.Control
                      type="text"
                      placeholder={t('first_name')}
                      value={inputs['first_name']}
                      onChange={onChangeHandler}
                      required
                      id="first_name_field"
                      name="first_name"
                    />
                  </Form.Group>
                  <Form.Group className={signInStyle['custom-form-group']}>
                    <Form.Control
                      type="text"
                      placeholder={t('last_name')}
                      required
                      id="last_name_field"
                      name="last_name"
                      value={inputs['last_name']}
                      onChange={onChangeHandler}
                    />
                  </Form.Group>
                </div>
                <Form.Group className={signInStyle['custom-form-group']}>
                  <Form.Control
                    type="email"
                    placeholder={t('email')}
                    required
                    id="email_field"
                    name="email"
                    value={inputs['email']}
                    onChange={onChangeHandler}
                    title="Enter a valid email address"
                    pattern={
                      '[A-Za-z0-9._\\%\\+\\-]+@[A-Za-z0-9\\.\\-]+.[A-Za-z]{2,}$'
                    }
                  />
                </Form.Group>
                <Form.Group className={signInStyle['custom-form-group']}>
                  <Form.Control
                    type="tel"
                    placeholder={t('phone')}
                    required
                    id="phone_field"
                    name="phone"
                    value={inputs['phone']}
                    onChange={onChangeHandlerPhone}
                    title="Enter a valid phone number"
                    pattern={'\\(\\d{3}\\) \\d{3}-\\d{4}'}
                  />
                </Form.Group>

                <div className={signInStyle['form-roles-container']}>
                  <Form.Group className={signInStyle['custom-form-group']}>
                    <Form.Control
                      type="text"
                      name="registered_role"
                      placeholder={t('registered_role')}
                      readOnly={true}
                      disabled={true}
                    />
                  </Form.Group>
                  <Form.Group className={signInStyle['custom-form-group']}>
                    <Form.Control
                      as="select"
                      id="registered_role_field"
                      name="registered_role"
                      className={signInStyle['custom-select']}
                      value={inputs['registered_role']}
                      onChange={onChangeHandler}
                    >
                      <option value={0}>{t('registered_role1')}</option>
                      <option value={1}>{t('registered_role2')}</option>
                    </Form.Control>
                  </Form.Group>
                </div>
              </div>

              <div className="text-center mb-4 mt-4">
                <CustomButton
                  label={t('register_next')}
                  className={`font-poppins custom-button-default ${signInStyle['width-50']}`}
                  type="button"
                  onClick={onNextStep}
                />
              </div>
            </div>

            <div
              id="step2"
              style={{ display: step2Visibility ? 'block' : 'none' }}
            >
              <div className={signInStyle['b-form-body']}>
                {inputs['registered_role'] == 0 && (
                  <Form.Group
                    className={`${signInStyle['custom-form-group']} position-relative`}
                  >
                    <Form.Control
                      type="text"
                      placeholder={t('professional_id')}
                      value={inputs['professional_id']}
                      onChange={onChangeHandler}
                      name="professional_id"
                    />
                    <OverlayTrigger
                      placement="right"
                      delay={{ show: 250, hide: 400 }}
                      overlay={renderTooltip}
                    >
                      <span style={customStyles.iconWrapper}>
                        <FontAwesomeIcon icon={faInfoCircle} size="lg" />
                      </span>
                    </OverlayTrigger>
                  </Form.Group>
                )}

                {inputs['registered_role'] == 1 && (
                  <Form.Group className={signInStyle['custom-form-group']}>
                    <Form.Control
                      as="input"
                      id={`youngest_child_yob`}
                      label={t('youngestChildYOB')}
                      onChange={onChangeHandler}
                      onClick={(e) => {
                        e.preventDefault(e);
                        e.stopPropagation(e);
                        setOpenModal(true);
                      }}
                      name="youngest_child_yob"
                      placeholder={t('youngestChildYOB')}
                      onKeyDown={() => false}
                      // readOnly
                      required
                      pattern={'^\\d{4}$'}
                      value={inputs['youngest_child_yob'] || ''}
                      style={{
                        height: '100%',
                        borderRadius: '0.5rem',
                        borderColor: 'transparent',
                        caretColor: 'transparent',
                      }}
                    />
                  </Form.Group>
                )}

                <Form.Group className={signInStyle['custom-form-group']}>
                  <Form.Control
                    type="password"
                    placeholder={t('password')}
                    value={inputs['password']}
                    onChange={onChangeHandler}
                    required
                    name="password"
                  />
                </Form.Group>
                <Form.Group className={signInStyle['custom-form-group']}>
                  <Form.Control
                    type="password"
                    placeholder={t('confirm_password')}
                    value={inputs['confirmPassword']}
                    onChange={onChangeHandler}
                    required
                    name="confirmPassword"
                  />
                </Form.Group>
                <Form.Group className={signInStyle['custom-form-group']}>
                  <Form.Control
                    type="text"
                    placeholder={t('registration_code')}
                    required
                    value={inputs['registration_code']}
                    onChange={onChangeHandler}
                    name="registration_code"
                  />
                </Form.Group>
              </div>

              <div className="text-center mb-4 mt-4">
                <CustomButton
                  label={t('sign_up2')}
                  className={`font-poppins custom-button-default ${
                    signInStyle['width-50']
                  } ${isFormValid ? 'disabled' : ''}`}
                  type="submit"
                />
                {successMessage !== '' && (
                  <p className="m-0 fw-500 font-poppins mt-4 mb-3 text-color-accent">
                    {successMessage}
                  </p>
                )}
              </div>

              <div className={signInStyle['check-box-wrapper-center']}>
                <div className={signInStyle['check-box-check-container']}>
                  <Form.Check
                    id={`terms`}
                    type="checkbox"
                    required
                    checked={isTCAccepted}
                    onChange={() => {
                      if (!isTCAccepted) {
                        setCheckIsTCAccepted('');
                      }

                      setIsTCAccepted((isTermsAccepted) => !isTermsAccepted);
                    }}
                  />
                  <label
                    htmlFor={`terms`}
                    className={`mb-0 font-14 fw-500 text-color-primary font-poppins`}
                  >
                    {t('i_agree')}{' '}
                    <span onClick={toggle}>
                      <button
                        className={`font-poppins font-14 fw-500 text-color-accent ${signInStyle['clear-btn']}`}
                      >
                        {t('t_c')}
                      </button>
                    </span>
                  </label>
                </div>
              </div>

              {checkTCAccepted !== '' ? (
                <p className="text-danger text-center fw-500">
                  {checkTCAccepted}
                </p>
              ) : (
                ''
              )}

              {isLoading && (
                <div className={`my-2`}>
                  <CustomSpinner />
                </div>
              )}
            </div>

            <div className="text-center text-danger">{passwordError}</div>
            <div className="text-center text-danger">{phoneError}</div>
            <div className="text-center text-danger">
              {youngestChildYobError}
            </div>

            <TermsConditionsModal
              isOpen={modal}
              isTCAccepted={isTCAccepted}
              handleClose={toggle}
              onAcceptTC={_onAcceptTC}
              modalTitle={t('t_c')}
              modalContent={
                i18n.language === 'en' ? (
                  <TermsAndConditions_en />
                ) : (
                  <TermsAndConditions_es />
                )
              }
              showFooter={true}
              fullscreen={true}
            />
            <div className="text-center pt-2">
              <p className="font-poppins font-18 fw-500 text-color-primary">
                <span>
                  <Link
                    to="/signin"
                    className="font-poppins font-18 fw-500 text-color-accent"
                  >
                    {t('already_account')}
                    {t('sign_in2')}
                  </Link>
                </span>
              </p>
            </div>
          </Form>
        </div>

        <div className={styles['dashboard-footer']}>
          <p className="font-13 font-poppins .text-color-tertiary m-0">
            {/* All Rights Reserved © {new Date().getFullYear()} {COMPANY_LEGAL_NAME} */}
            © 2025 Nashville Public Library Foundation
            <span className="text-color-secondary font-weight-bolder mx-2">
              |
            </span>{' '}
            <a
              className={`font-13 font-poppins text-color-tertiary text-decoration-none`}
              target="_blank"
              href="/privacy_policy"
            >
              {t('privacy_policy')}
            </a>
            <span className="text-color-secondary font-weight-bolder mx-2">
              |
            </span>{' '}
            <a
              className={`font-13 font-poppins text-color-tertiary text-decoration-none`}
              target="_blank"
              href="/deleteaccount"
            >
              {t('delete_account')}
            </a>
            <br></br> <br></br>
            <DownloadLinks />
          </p>

          <p className="font-13 font-poppins text-color-tertiary m-0">
            {t('need_help')}
            <span className="text-color-secondary font-weight-bolder mx-2">
              |
            </span>{' '}
            <a
              className={`font-13 font-poppins text-color-tertiary text-decoration-none`}
              href={'mailto:' + PRODUCT_SUPPORT_EMAIL}
            >
              {PRODUCT_SUPPORT_EMAIL}
            </a>
            <br></br>
            <br></br>
            <br></br>&nbsp;
          </p>
        </div>
      </div>
    </>
  );
};

export default SignUpForm;
